/**
 * Commerce Redux Slice - Payment processing, subscriptions, and billing management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { commerceService, PricingPlan, Subscription, PaymentMethod, Invoice, PaymentIntent } from '../../services/commerce';

interface CommerceState {
  // Pricing Plans
  pricingPlans: PricingPlan[];
  plansLoading: boolean;

  // Subscription
  currentSubscription: Subscription | null;
  subscriptionLoading: boolean;
  subscriptionError: string | null;

  // Payment Methods
  paymentMethods: PaymentMethod[];
  paymentMethodsLoading: boolean;

  // Invoices
  invoices: Invoice[];
  invoicesLoading: boolean;
  invoicesPage: number;
  invoicesHasMore: boolean;

  // Payment Processing
  currentPaymentIntent: PaymentIntent | null;
  paymentLoading: boolean;
  paymentError: string | null;

  // Billing Portal
  billingPortalLoading: boolean;

  // Usage and Analytics
  usageData: {
    functions: number;
    storage: number;
    apiCalls: number;
    resetDate: string;
  } | null;
  usageLoading: boolean;

  // Revenue Analytics (for developers)
  revenueStats: {
    totalRevenue: number;
    revenueByDay: { date: string; revenue: number }[];
    topFunctions: Array<{
      functionId: string;
      name: string;
      revenue: number;
      sales: number;
    }>;
    payout: {
      available: number;
      pending: number;
      nextPayoutDate: string;
    };
  } | null;
  revenueLoading: boolean;
}

const initialState: CommerceState = {
  pricingPlans: [],
  plansLoading: false,

  currentSubscription: null,
  subscriptionLoading: false,
  subscriptionError: null,

  paymentMethods: [],
  paymentMethodsLoading: false,

  invoices: [],
  invoicesLoading: false,
  invoicesPage: 1,
  invoicesHasMore: true,

  currentPaymentIntent: null,
  paymentLoading: false,
  paymentError: null,

  billingPortalLoading: false,

  usageData: null,
  usageLoading: false,

  revenueStats: null,
  revenueLoading: false
};

// Async Thunks

// Pricing Plans
export const loadPricingPlans = createAsyncThunk(
  'commerce/loadPricingPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await commerceService.getPricingPlans();
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load pricing plans');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load pricing plans');
    }
  }
);

// Subscription Management
export const loadCurrentSubscription = createAsyncThunk<
  Subscription | null,
  void,
  { rejectValue: string }
>(
  'commerce/loadCurrentSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await commerceService.getCurrentSubscription();
      if (response.status === 'success') {
        return response.data ?? null;
      }
      throw new Error('Failed to load subscription');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load subscription');
    }
  }
);

export const createSubscription = createAsyncThunk<
  { subscription: Subscription; paymentIntent?: PaymentIntent },
  { planId: string; paymentMethodId?: string; trialDays?: number },
  { rejectValue: string }
>(
  'commerce/createSubscription',
  async (params: { planId: string; paymentMethodId?: string; trialDays?: number }, { rejectWithValue }) => {
    try {
      const response = await commerceService.createSubscription(params.planId, params.paymentMethodId, params.trialDays);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to create subscription');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create subscription');
    }
  }
);

export const updateSubscription = createAsyncThunk<
  Subscription,
  string,
  { rejectValue: string }
>(
  'commerce/updateSubscription',
  async (planId: string, { rejectWithValue }) => {
    try {
      const response = await commerceService.updateSubscription(planId);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to update subscription');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update subscription');
    }
  }
);

export const cancelSubscription = createAsyncThunk<
  Subscription,
  boolean | undefined,
  { rejectValue: string }
>(
  'commerce/cancelSubscription',
  async (immediate: boolean = false, { rejectWithValue }) => {
    try {
      const response = await commerceService.cancelSubscription(immediate);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to cancel subscription');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to cancel subscription');
    }
  }
);

// Payment Methods
export const loadPaymentMethods = createAsyncThunk(
  'commerce/loadPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await commerceService.getPaymentMethods();
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load payment methods');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load payment methods');
    }
  }
);

export const addPaymentMethod = createAsyncThunk(
  'commerce/addPaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const response = await commerceService.addPaymentMethod(paymentMethodId);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to add payment method');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add payment method');
    }
  }
);

export const removePaymentMethod = createAsyncThunk(
  'commerce/removePaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const response = await commerceService.deletePaymentMethod(paymentMethodId);
      if (response.status === 'success') {
        return paymentMethodId;
      }
      throw new Error('Failed to remove payment method');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove payment method');
    }
  }
);

export const setDefaultPaymentMethod = createAsyncThunk(
  'commerce/setDefaultPaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const response = await commerceService.setDefaultPaymentMethod(paymentMethodId);
      if (response.status === 'success') {
        return paymentMethodId;
      }
      throw new Error('Failed to set default payment method');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to set default payment method');
    }
  }
);

// Invoices
export const loadInvoices = createAsyncThunk(
  'commerce/loadInvoices',
  async (params: { page: number; limit: number }, { rejectWithValue }) => {
    try {
      const response = await commerceService.getInvoices(params.page, params.limit);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load invoices');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load invoices');
    }
  }
);

// Payment Processing
export const createPaymentIntent = createAsyncThunk(
  'commerce/createPaymentIntent',
  async (params: { amount: number; currency: string; metadata?: Record<string, string> }, { rejectWithValue }) => {
    try {
      const response = await commerceService.createPaymentIntent(params.amount, params.currency, params.metadata);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to create payment intent');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create payment intent');
    }
  }
);

// Usage Tracking
export const loadUsageData = createAsyncThunk(
  'commerce/loadUsageData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await commerceService.getSubscriptionUsage();
      if (response.status === 'success' && response.data) {
        if (response.data.current.length === 0) {
          return null;
        }

        const usageLookup = Object.fromEntries(
          response.data.current.map((record) => [record.metricType, record.quantity])
        ) as Record<'functions' | 'storage' | 'api_calls', number>;

        return {
          functions: usageLookup.functions ?? 0,
          storage: usageLookup.storage ?? 0,
          apiCalls: usageLookup['api_calls'] ?? 0,
          resetDate: response.data.period.end,
        };
      }
      throw new Error('Failed to load usage data');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load usage data');
    }
  }
);

// Revenue Analytics
export const loadRevenueStats = createAsyncThunk(
  'commerce/loadRevenueStats',
  async (period: '7d' | '30d' | '90d' | '1y' = '30d', { rejectWithValue }) => {
    try {
      const response = await commerceService.getRevenueStats(period);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load revenue stats');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load revenue stats');
    }
  }
);

// Billing Portal
export const openBillingPortal = createAsyncThunk(
  'commerce/openBillingPortal',
  async ({ returnUrl }: { returnUrl?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await commerceService.createBillingPortalSession(returnUrl);
      if (response.status === 'success' && response.data) {
        window.location.href = response.data.url;
        return response.data;
      }
      throw new Error('Failed to open billing portal');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to open billing portal');
    }
  }
);

const commerceSlice = createSlice({
  name: 'commerce',
  initialState,
  reducers: {
    clearPaymentError: (state) => {
      state.paymentError = null;
    },

    clearSubscriptionError: (state) => {
      state.subscriptionError = null;
    },

    clearPaymentIntent: (state) => {
      state.currentPaymentIntent = null;
    },

    updateUsage: (state, action: PayloadAction<{ metricType: 'functions' | 'storage' | 'api_calls'; quantity: number }>) => {
      if (!state.usageData) {
        return;
      }

      const { metricType, quantity } = action.payload;

      switch (metricType) {
        case 'functions':
          state.usageData.functions += quantity;
          break;
        case 'storage':
          state.usageData.storage += quantity;
          break;
        case 'api_calls':
          state.usageData.apiCalls += quantity;
          break;
        default:
          break;
      }
    }
  },
  extraReducers: (builder) => {
    // Pricing Plans
    builder
      .addCase(loadPricingPlans.pending, (state) => {
        state.plansLoading = true;
      })
      .addCase(loadPricingPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.pricingPlans = action.payload;
      })
      .addCase(loadPricingPlans.rejected, (state) => {
        state.plansLoading = false;
      });

    // Subscription
    builder
      .addCase(loadCurrentSubscription.pending, (state) => {
        state.subscriptionLoading = true;
      })
      .addCase(loadCurrentSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.currentSubscription = action.payload;
      })
      .addCase(loadCurrentSubscription.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload as string;
      });

    builder
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.currentSubscription = action.payload.subscription;
        state.currentPaymentIntent = action.payload.paymentIntent || null;
      });

    builder
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.currentSubscription = action.payload;
      });

    builder
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.currentSubscription = action.payload;
      });

    // Payment Methods
    builder
      .addCase(loadPaymentMethods.pending, (state) => {
        state.paymentMethodsLoading = true;
      })
      .addCase(loadPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethods = action.payload;
      })
      .addCase(loadPaymentMethods.rejected, (state) => {
        state.paymentMethodsLoading = false;
      });

    builder
      .addCase(addPaymentMethod.fulfilled, (state, action) => {
        state.paymentMethods.push(action.payload);
      });

    builder
      .addCase(removePaymentMethod.fulfilled, (state, action) => {
        state.paymentMethods = state.paymentMethods.filter(pm => pm.id !== action.payload);
      });

    builder
      .addCase(setDefaultPaymentMethod.fulfilled, (state, action) => {
        state.paymentMethods = state.paymentMethods.map(pm => ({
          ...pm,
          isDefault: pm.id === action.payload
        }));
      });

    // Invoices
    builder
      .addCase(loadInvoices.pending, (state) => {
        state.invoicesLoading = true;
      })
      .addCase(loadInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;

        if (action.payload.page === 1) {
          state.invoices = action.payload.invoices;
        } else {
          state.invoices.push(...action.payload.invoices);
        }

        state.invoicesPage = action.payload.page;
        state.invoicesHasMore = action.payload.hasMore;
      })
      .addCase(loadInvoices.rejected, (state) => {
        state.invoicesLoading = false;
      });

    // Payment Processing
    builder
      .addCase(createPaymentIntent.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.currentPaymentIntent = action.payload;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload as string;
      });

    // Usage Data
    builder
      .addCase(loadUsageData.pending, (state) => {
        state.usageLoading = true;
      })
      .addCase(loadUsageData.fulfilled, (state, action) => {
        state.usageLoading = false;
        state.usageData = action.payload;
      })
      .addCase(loadUsageData.rejected, (state) => {
        state.usageLoading = false;
      });

    // Revenue Stats
    builder
      .addCase(loadRevenueStats.pending, (state) => {
        state.revenueLoading = true;
      })
      .addCase(loadRevenueStats.fulfilled, (state, action) => {
        state.revenueLoading = false;
        state.revenueStats = action.payload;
      })
      .addCase(loadRevenueStats.rejected, (state) => {
        state.revenueLoading = false;
      });

    // Billing Portal
    builder
      .addCase(openBillingPortal.pending, (state) => {
        state.billingPortalLoading = true;
      })
      .addCase(openBillingPortal.fulfilled, (state) => {
        state.billingPortalLoading = false;
      })
      .addCase(openBillingPortal.rejected, (state) => {
        state.billingPortalLoading = false;
      });
  }
});

export const {
  clearPaymentError,
  clearSubscriptionError,
  clearPaymentIntent,
  updateUsage
} = commerceSlice.actions;

export default commerceSlice.reducer;
